// AdminUserDetail — full edit page for a single player.
//
// Properly rewritten for light + dark with explicit per-element colors
// on every button (Activate / Suspend / Ban / Delete / Save / Reset /
// Credit / Debit / Reset password / Change owner) and every ledger
// chip / amount color so nothing fades into the background in light
// mode.
//
// Sections (single page, gridded on lg+):
//   • Identity card (avatar + status pill + key fields + wallet stat
//     + status buttons + ownership + delete)
//   • Wallet adjust card (credit/debit form with mark-as-recharge)
//   • Password reset card
//   • Ledger card
//   • Profile editor (full form, spans 3 cols)

import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import AvatarCropper from '@baaligames/shared-ui/components/AvatarCropper';
import Icon from '@baaligames/shared-ui/components/Icon';
import PhoneContact from '@baaligames/shared-ui/components/PhoneContact';
import { adminApi, getAdminAccessToken } from '@baaligames/shared-ui/admin/lib/adminApi';
import { compressForUpload } from '@baaligames/shared-ui/lib/imageCompress';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import { getGame } from '@baaligames/shared-ui/games/registry';
// Phase 16.8: tabbed layout. Same SegmentedTabs primitive used by
// AdminAgentDetail and AgentSubAgentDetail so the player edit page
// feels consistent with agent edit pages.
import { SegmentedTabs, type TabDef } from '@baaligames/shared-ui/admin/components/TabHub';
// R4 final cleanup (Bug B parity) — agent-frontend variant of this
// page now matches admin-frontend's: free-text address inputs out,
// LocationCapture in. Agents (parent agents) editing their owned
// players use the same structured-geo flow admin uses.
import LocationCapture, {
  type GeoBag,
} from '@baaligames/shared-ui/components/LocationCapture';
// Phase R5 — agent self-handoff via UAID. Agent can transfer any
// player they currently own to another agent (in or out of their
// downline) by typing the destination's UAID.
import PlayerTransferModal from '@baaligames/shared-ui/admin/components/PlayerTransferModal';
import AgentReassignSheet from '../components/AgentReassignSheet';
// R5 hotfix — after-transfer success message must survive the
// route change (the local `note()` toast is component-scoped and
// disappears the instant we navigate away from the now-
// inaccessible /people/players/:id page). The global useToast
// provider is mounted at the agent-frontend root, so its toasts
// persist across route transitions.
import { useToast } from '@baaligames/shared-ui/lib/toast';

type UserFull = {
  id: number;
  username: string;
  email: string;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  birthdate: string | null;
  gender: 'male' | 'female' | 'other' | 'prefer_not' | null;
  // Structured geo. Migration 079 (2026-05-16) dropped the seven
  // legacy free-text address columns; LocationCapture drives the
  // entire flow from the FK + lat/lng bag below.
  //
  // `pincode` (6-digit string) is JOINed in from the `pincodes`
  // table by UserModel.getForAdmin — it's NOT a column on `users`
  // itself. Surfaced so LocationCapture's saved-summary card can
  // render "Pin code: NNNNNN" instantly.
  pincode: string | null;
  latitude: string | number | null;
  longitude: string | number | null;
  state_id: number | null;
  district_id: number | null;
  area_id: number | null;
  pincode_id: number | null;
  nation: string | null;
  geo_updated_at: string | null;
  profile_image_url: string | null;
  agent_id: number | null;
  owner_username: string | null;
  owner_avatar_url: string | null;
  telegram_chat_id: number | null;
  telegram_username: string | null;
  telegram_verified_at: string | null;
  phone_verified_at: string | null;
  status: 'pending' | 'active' | 'suspended' | 'banned';
  email_verified_at: string | null;
  failed_login_attempts: number;
  locked_until: string | null;
  created_at: string;
  last_login_at: string | null;
  updated_at: string;
  wallet_balance: number;
};

type LedgerRow = {
  id: number;
  game: string | null;
  kind: string;
  amount: number;
  note: string | null;
  actor_admin_id: number | null;
  balance_after: number | null;
  session_id: number | null;
  created_at: string;
};

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

// ── Status pill colors (used both on the identity card and
//    inside the status-button row).
const STATUS_PILL: Record<UserFull['status'], string> = {
  pending:   'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-400/10 dark:text-amber-300 dark:border-amber-400/30',
  active:    'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-400/10 dark:text-emerald-300 dark:border-emerald-400/30',
  suspended: 'bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-400/15 dark:text-amber-200 dark:border-amber-400/40',
  banned:    'bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-400/10 dark:text-rose-300 dark:border-rose-400/30',
};

// ── Ledger row metadata. The text color is the AMOUNT direction,
// shown with proper light + dark contrast.
// Ledger kind metadata. The display label is resolved via t() at row
// render time (see useGameBadge / KIND_LABEL_KEY); only the icon +
// signed-direction stays static here.
const KIND_LABEL_KEY: Record<string, string> = {
  bet:               'agent.playerDetail.ledger.kind.bet',
  win:               'agent.playerDetail.ledger.kind.win',
  refund:            'agent.playerDetail.ledger.kind.refund',
  fee:               'agent.playerDetail.ledger.kind.fee',
  admin_credit:      'agent.playerDetail.ledger.kind.adminCredit',
  admin_debit:       'agent.playerDetail.ledger.kind.adminDebit',
  recharge:          'agent.playerDetail.ledger.kind.recharge',
  referral_bonus:    'agent.playerDetail.ledger.kind.referralBonus',
  signup_bonus:      'agent.playerDetail.ledger.kind.signupBonus',
  offer_credit:      'agent.playerDetail.ledger.kind.offerCredit',
  withdrawal_hold:   'agent.playerDetail.ledger.kind.withdrawalHold',
  withdrawal_refund: 'agent.playerDetail.ledger.kind.withdrawalRefund',
};
const KIND_META: Record<string, { icon: string; pos: boolean | null }> = {
  bet:            { icon: 'trending_down',          pos: false },
  win:            { icon: 'trending_up',            pos: true  },
  refund:         { icon: 'undo',                   pos: true  },
  fee:            { icon: 'receipt_long',           pos: false },
  admin_credit:   { icon: 'add_circle',             pos: true  },
  admin_debit:    { icon: 'remove_circle',          pos: false },
  recharge:       { icon: 'account_balance_wallet', pos: true  },
  referral_bonus: { icon: 'card_giftcard',          pos: true  },
  signup_bonus:   { icon: 'stars',                  pos: true  },
  offer_credit:   { icon: 'redeem',                 pos: true  },
  withdrawal_hold:   { icon: 'north_east',          pos: false },
  withdrawal_refund: { icon: 'undo',                pos: true  },
};

// Game badge resolver. The `platform` pseudo-game has its own
// translated label; real games keep their `displayName` (those come
// from the games registry and are typically left untranslated — the
// game name is a brand and travels as-is across languages).
function useGameBadge() {
  const t = useT();
  return (id: string | null): { label: string; cls: string } | null => {
    if (!id) return null;
    if (id === 'platform') {
      return { label: t('agent.playerDetail.ledger.game.platform'), cls: 'bg-arena-100/60 text-arena-700 border border-arena-200/60 dark:bg-arena-700/40 dark:text-neon-200/80 dark:border-arena-600/50' };
    }
    const g = getGame(id);
    if (!g) return { label: id, cls: 'bg-arena-100/60 text-arena-700 border border-arena-200/60 dark:bg-arena-700/40 dark:text-neon-200/80 dark:border-arena-600/50' };
    return {
      label: g.displayName,
      cls: 'bg-violet-50 text-violet-700 border border-violet-200 dark:bg-violet-400/10 dark:text-violet-300 dark:border-violet-400/30',
    };
  };
}

// ─── Tab definitions (Phase 16.8) ──────────────────────────────────
//
// Six tabs mirror the AdminAgentDetail vocabulary. Players don't have
// payment_config / partnership / permissions / locks so the tab list
// is shorter, but the visual + interaction model is the same.

type PlayerTab = 'identity' | 'profile' | 'wallet' | 'security' | 'ownership' | 'operations';

// Phase 17 FIX: tabs filtered per role inside the component (see
// `visibleTabs` below). Agents don't see the Ownership tab because
// the OwnershipCard returns null for non-admin role — leaving the
// tab visible just rendered an empty white "Owner agent" header
// card with nothing inside.
// Per-tab metadata. Labels are looked up via t() at render time in
// AdminUserDetail() — keeping the static array here as the source of
// truth for tab order + key + icon.
const PLAYER_TABS_META: Array<{ key: PlayerTab; labelKey: string; icon: string }> = [
  { key: 'identity',   labelKey: 'agent.playerDetail.tab.identity',   icon: 'badge' },
  { key: 'profile',    labelKey: 'agent.playerDetail.tab.profile',    icon: 'person' },
  { key: 'wallet',     labelKey: 'agent.playerDetail.tab.wallet',     icon: 'account_balance_wallet' },
  { key: 'security',   labelKey: 'agent.playerDetail.tab.security',   icon: 'lock' },
  { key: 'ownership',  labelKey: 'agent.playerDetail.tab.ownership',  icon: 'support_agent' },
  { key: 'operations', labelKey: 'agent.playerDetail.tab.operations', icon: 'manage_accounts' },
];

// ─── Page ──────────────────────────────────────────────────────────

export default function AdminUserDetail() {
  const t = useT();
  const can = useAdminAuth().canDo;
  const role = useAdminAuth().admin?.role;
  // Phase R5 hotfix — the logged-in operator's id (admin OR agent).
  // Used to gate the in-Identity-tab Transfer button: agents see it
  // only when they CURRENTLY own this player; admins always see it.
  // The button used to live in the Ownership tab, but that tab is
  // filtered out for non-admin role (line ~311's tab whitelist), so
  // agents never reached the Transfer CTA at all.
  const meId = useAdminAuth().admin?.id;
  // Global toast for messages that need to survive the post-transfer
  // navigation (the local `note()` toast unmounts with the page).
  const { push: pushToast } = useToast();
  const { id } = useParams<{ id: string }>();
  const uid = Number(id);
  const nav = useNavigate();
  const [user, setUser] = useState<UserFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [cropping, setCropping] = useState(false);
  const [tab, setTab] = useState<PlayerTab>('identity');
  // Phase R5 — UAID-keyed handoff modal. Lives at the page level
  // (not inside OwnershipCard) because for agents the existing
  // OwnershipCard returns null entirely; we want a meaningful CTA
  // in the ownership tab regardless of role.
  const [transferring, setTransferring] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { user: u } = await adminApi.get<{ user: UserFull }>(`/admin/users/${uid}`);
      setUser(u);
    } catch (e) {
      setErr((e as ApiError).message || t('agent.playerDetail.error.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [uid, t]);

  useEffect(() => { load(); }, [load]);

  function note(msg: string) {
    setFlash(msg);
    setTimeout(() => setFlash(null), 3000);
  }

  async function uploadPlayerAvatar(rawFile: File) {
    if (!user) return;
    try {
      const file = await compressForUpload(rawFile, { preset: 'avatar' });
      const fd = new FormData();
      fd.append('avatar', file);
      // `tok` shadows the outer `t` translator on purpose — the bearer
      // token name was originally `t`; renaming it locally lets us keep
      // the surrounding code idiomatic.
      const tok = getAdminAccessToken();
      const res = await fetch(`/api/admin/users/${user.id}/avatar`, {
        method: 'POST', credentials: 'include',
        headers: tok ? { authorization: `Bearer ${tok}` } : {},
        body: fd,
      });
      const j = await res.json();
      if (!j.ok) throw new Error(j.error?.message || t('agent.playerDetail.avatar.error.uploadFailed'));
      note(t('agent.playerDetail.avatar.toast.updated'));
      await load();
    } catch (e) {
      alert((e as Error).message);
      throw e;
    }
  }

  // Resolve tab labels via t() at render so language flips reactively.
  const PLAYER_TABS: TabDef[] = PLAYER_TABS_META.map((m) => ({ key: m.key, label: t(m.labelKey), icon: m.icon }));

  return (
    <div className="max-w-screen-2xl mx-auto px-2 sm:px-3 lg:px-6 py-3 lg:py-4 space-y-4">
      {/* Back */}
      <div className="px-2 lg:px-4 pt-1">
        <Link to="/people/players/list"
              className="inline-flex items-center gap-1 text-[12px] font-semibold
                         text-arena-700 hover:text-arena-900
                         dark:text-neon-200/80 dark:hover:text-white transition">
          <Icon name="arrow_back" size={14} /> {t('agent.playerDetail.backToPlayers')}
        </Link>
      </div>

      {loading && (
        <div className="px-2 lg:px-4">
          <div className="rounded-2xl bg-white dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/50 p-10 text-center text-[13px] text-arena-600 dark:text-neon-200/80">
            <Icon name="progress_activity" size={20} className="animate-spin inline-block mr-1.5" /> {t('agent.playerDetail.loading')}
          </div>
        </div>
      )}

      {err && (
        <Banner tone="error">{err}</Banner>
      )}

      {flash && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}>
          <Banner tone="success"><Icon name="check_circle" size={14} filled className="inline -mt-0.5 mr-1" /> {flash}</Banner>
        </motion.div>
      )}

      {!loading && user && (
        <div className="px-2 lg:px-4 space-y-4">
          {/* ─── Phase 16.8: header card (always visible) ────────
              Avatar + name + @username + status pill + wallet
              balance chip. Mirrors the AdminAgentDetail header so
              the player edit page feels consistent with the agent
              edit page. */}
          <Card>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="relative shrink-0">
                <AdminAvatar url={user.profile_image_url} username={user.username} size="lg" />
                {can('users','edit') && <button
                  onClick={() => setCropping(true)}
                  title={t('agent.playerDetail.avatar.changeTitle')}
                  className="absolute -bottom-1 -right-1 grid place-items-center w-7 h-7 rounded-full
                             bg-violet-600 hover:bg-violet-500 active:bg-violet-700
                             text-white shadow-md
                             focus:outline-none focus:ring-2 focus:ring-violet-400/60 focus:ring-offset-2
                             focus:ring-offset-white dark:focus:ring-offset-neutral-950 transition active:scale-95"
                >
                  <Icon name="photo_camera" size={14} />
                </button>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold text-arena-900 dark:text-white truncate">
                    {user.first_name || user.last_name
                      ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                      : t('agent.playerDetail.header.noName')}
                  </h2>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold capitalize ${STATUS_PILL[user.status]}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-current" />
                    {t(`agent.playerDetail.status.${user.status}`)}
                  </span>
                </div>
                <div className="text-[11.5px] text-arena-600 dark:text-neon-200/80">
                  {(((user.first_name || "") + " " + (user.last_name || "")).trim() || "—")} · #{user.id}
                </div>
                <div className="mt-1 text-[11px] text-arena-600 dark:text-neon-200/80">
                  {t('agent.playerDetail.header.joined', { date: new Date(user.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) })}
                  {user.owner_username && <>{t('agent.playerDetail.header.ownedBy.prefix')}<span className="text-arena-700 dark:text-neon-200/85 font-semibold">{(((user as { owner_display_name?: string }).owner_display_name) || "").trim() || "—"}</span></>}
                </div>
              </div>
              <div className="shrink-0 sm:text-right">
                <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/80">{t('agent.playerDetail.header.walletLabel')}</div>
                <div className="font-display font-black text-2xl tabular-nums text-violet-700 dark:text-violet-300">
                  {inr(user.wallet_balance)}
                </div>
              </div>
            </div>
          </Card>

          {/* ─── Phase 16.8: tab strip ──────────────────────── */}
          {/* Phase 17 FIX: hide Ownership tab for non-admin roles —
              OwnershipCard returns null for agents so the tab body
              would be empty. */}
          <SegmentedTabs
            variant="block"
            tabs={PLAYER_TABS.filter(t => role === 'admin' || t.key !== 'ownership')}
            activeKey={tab}
            onTabChange={(k) => setTab(k as PlayerTab)}
          />

          {/* ─── Tab body ─────────────────────────────────── */}

          {tab === 'identity' && (
            <Card>
              <h3 className="font-semibold text-[14px] text-arena-900 dark:text-white inline-flex items-center gap-2 mb-3">
                <Icon name="badge" size={16} className="text-violet-500" />
                {t('agent.playerDetail.identity.title')}
              </h3>
              <dl className="text-[12px] space-y-1.5">
                <Info k={t('agent.playerDetail.identity.row.username')} v={`@${user.username}`} />
                <Info k={t('agent.playerDetail.identity.row.email')}    v={user.email} />
                {user.phone ? (
                  <div className="flex items-center justify-between gap-2 py-1 border-b border-arena-200/40 dark:border-arena-600/40 last:border-b-0">
                    <dt className="text-arena-600 dark:text-neon-200/80">{t('agent.playerDetail.identity.row.phone')}</dt>
                    <dd className="ml-2 min-w-0">
                      <PhoneContact phone={user.phone} name={user.first_name || user.username} iconSize={12}
                        className="text-arena-900 dark:text-white tabular-nums" />
                    </dd>
                  </div>
                ) : (
                  <Info k={t('agent.playerDetail.identity.row.phone')} v={t('agent.playerDetail.identity.value.dash')} />
                )}
                <Info k={t('agent.playerDetail.identity.row.country')}  v={user.nation || 'India'} />
                <Info k={t('agent.playerDetail.identity.row.joined')}   v={new Date(user.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} />
                <Info k={t('agent.subAgentDetail.identity.row.lastLogin')} v={user.last_login_at ? new Date(user.last_login_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : t('agent.playerDetail.identity.value.dash')} />
                <Info k={t('agent.playerDetail.identity.row.emailVerified')} v={t(user.email_verified_at ? 'agent.playerDetail.identity.value.yes' : 'agent.playerDetail.identity.value.no')} accent={user.email_verified_at ? 'ok' : 'warn'} />
                <Info k={t('agent.playerDetail.identity.row.telegram')} v={user.telegram_verified_at && user.telegram_username ? `@${user.telegram_username}` : t('agent.playerDetail.identity.value.dash')} />
                <Info k={t('agent.playerDetail.identity.row.failedLogins')}  v={String(user.failed_login_attempts)} accent={user.failed_login_attempts > 0 ? 'warn' : undefined} />
                {user.locked_until && <Info k={t('agent.playerDetail.identity.row.lockedUntil')} v={new Date(user.locked_until).toLocaleString()} accent="warn" />}
              </dl>

              {/* Phase R5 hotfix — Transfer-by-UAID CTA inside the
                  Identity tab so the agent-frontend actually surfaces
                  it. Previous placement was inside the Ownership tab
                  which is filtered out of PLAYER_TABS for non-admin
                  role (see line 311 above) — agents could neither
                  click nor even see the button.

                  Visibility gate (BOTH conditions met):
                    1. We know who the logged-in operator is (meId)
                       AND the player has an owner — otherwise there
                       is no transfer relationship to mutate from.
                    2. EITHER the operator is admin (admin override
                       — backend has no ownership check) OR they
                       currently own this player (agent self-handoff
                       — backend's 404 ownership check would otherwise
                       reject the request).

                  We deliberately do NOT show the button when
                  user.agent_id is null (platform-direct player) OR
                  when an agent is viewing a player they don't own.
                  Both states would render a button that the backend
                  immediately 404s, which is worse UX than not
                  showing the button at all. */}
              {meId != null && user.agent_id != null &&
                (role === 'admin' || Number(user.agent_id) === Number(meId)) && (
                <div className="mt-4 pt-4 border-t border-arena-200/40 dark:border-arena-600/50">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[12px] font-extrabold uppercase tracking-wider text-arena-700 dark:text-neon-200/85">
                        {t('agent.playerDetail.identity.transfer.title')}
                      </div>
                      <div className="text-[11.5px] text-arena-600 dark:text-neon-200/80 mt-0.5 leading-snug">
                        {t('agent.playerDetail.identity.transfer.body')}
                      </div>
                    </div>
                    <button
                      onClick={() => setTransferring(true)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-bold
                                 bg-cyan-600 hover:bg-cyan-500 active:bg-cyan-700
                                 text-white shadow-[0_2px_10px_rgba(6,182,212,0.4)]
                                 transition active:scale-95 shrink-0">
                      <Icon name="swap_horiz" size={13} /> {t('agent.playerDetail.identity.transfer.cta')}
                    </button>
                  </div>
                </div>
              )}
            </Card>
          )}

          {tab === 'profile' && (
            <ProfileEditCard user={user} onSaved={load} note={note} />
          )}

          {tab === 'wallet' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Mint/burn (credit · debit · mark-as-recharge) is admin-
                  only by design — the API rejects agents with 403. Show
                  the dedicated Path A recharge CTA instead, which debits
                  the agent's own pool transactionally. */}
              {role === 'admin'
                ? <WalletAdjustCard userId={user.id} onChanged={load} note={note} />
                : <RechargePlayerCTACard userId={user.id} username={user.username} />}
              <LedgerCard userId={user.id} />
            </div>
          )}

          {tab === 'security' && (
            <PasswordResetCard userId={user.id} note={note} />
          )}

          {tab === 'ownership' && (
            <Card>
              <h3 className="font-semibold text-[14px] text-arena-900 dark:text-white inline-flex items-center gap-2 mb-3">
                <Icon name="support_agent" size={16} className="text-violet-500" />
                {t('agent.playerDetail.ownership.title')}
              </h3>
              {/* Note: PLAYER_TABS.filter at line ~311 strips this
                  tab for non-admin role, so this branch only renders
                  for admin. The Transfer-by-UAID CTA was previously
                  duplicated here — moved to the Identity tab so the
                  agent-frontend (which never sees this tab) can
                  reach it. Admin still has the same control via
                  admin-frontend's AdminUserDetail.tsx. */}
              <OwnershipCard user={user} reload={load} note={note} />
            </Card>
          )}

          {tab === 'operations' && (
            <div className="space-y-4">
              <Card>
                <h3 className="font-semibold text-[14px] text-arena-900 dark:text-white inline-flex items-center gap-2 mb-3">
                  <Icon name="manage_accounts" size={16} className="text-violet-500" />
                  {t('agent.playerDetail.ops.accountStatus.title')}
                </h3>
                <p className="text-[11.5px] text-arena-600 dark:text-neon-200/80 mb-3">
                  {t('agent.playerDetail.ops.accountStatus.body')}
                </p>
                <StatusButtons user={user} reload={load} note={note} />
              </Card>
              {/* Phase 17 FIX: Danger zone is admin-only — DeleteButton
                  itself returns null for non-admin role, which left the
                  whole card rendering as just a header + helper text
                  with no button. Hide the entire card for agents. */}
              {role === 'admin' && (
                <Card>
                  <h3 className="font-semibold text-[14px] text-rose-700 dark:text-rose-300 inline-flex items-center gap-2 mb-3">
                    <Icon name="delete_forever" size={16} />
                    {t('agent.playerDetail.ops.danger.title')}
                  </h3>
                  <p className="text-[11.5px] text-arena-600 dark:text-neon-200/80 mb-3">
                    {t('agent.playerDetail.ops.danger.body')}
                  </p>
                  <DeleteButton user={user} onDeleted={() => nav('/people/players/list', { replace: true })} note={note} />
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {cropping && user && (
        <AvatarCropper
          title={t('agent.playerDetail.avatar.cropTitle', { username: user.username })}
          onUpload={uploadPlayerAvatar}
          onClose={() => setCropping(false)}
        />
      )}

      {/* Phase R5 — Transfer modal mount. Backend enforces that
          agents can only transfer players they CURRENTLY OWN
          (404 otherwise). The button that opens this modal lives
          inside the Identity tab (R5 hotfix moved it there from
          the role-gated Ownership tab). */}
      {transferring && user && (
        <PlayerTransferModal
          player={{ id: user.id, username: user.username }}
          onClose={() => setTransferring(false)}
          onTransferred={() => {
            // R5 hotfix — after a successful transfer the operator
            // who initiated it has DIFFERENT levels of continuing
            // access to the player:
            //   * admin   → still has full read/write (admin role
            //               bypasses the ownership check). Reload
            //               in place so the new owner shows up
            //               inline.
            //   * agent   → just lost ownership. The very next
            //               GET /admin/users/:id from this page's
            //               load() returns 404 (intentional auth
            //               check, see adminUsers.controller —
            //               no info leak about players outside
            //               the agent's pool). That 404 used to
            //               render as "User not found" + leave
            //               the stale player visible until manual
            //               refresh, making operators wonder
            //               whether the transfer actually went
            //               through. Fix: navigate to the players
            //               list and surface the success via the
            //               GLOBAL toast (the local `note()` toast
            //               unmounts with the page).
            if (role === 'admin') {
              note(t('agent.playerDetail.transfer.toast.admin'));
              load();
            } else {
              pushToast('success', t('agent.playerDetail.transfer.toast.agent', { username: user.username }));
              nav('/people/players', { replace: true });
            }
          }}
        />
      )}
    </div>
  );
}

// ─── Reusable shells ───────────────────────────────────────────────

function Card({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-2xl p-5
                 bg-white dark:bg-arena-700/40
                 border border-arena-200/60 dark:border-arena-600/50
                 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none">
      {children}
    </motion.div>
  );
}

function Banner({ tone, children }: { tone: 'error' | 'success' | 'warn' | 'info'; children: React.ReactNode }) {
  const M = {
    error:   'bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-500/10 dark:border-rose-400/30 dark:text-rose-200',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-400/30 dark:text-emerald-200',
    warn:    'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-500/10 dark:border-amber-400/30 dark:text-amber-200',
    info:    'bg-arena-50/40 border-arena-200/60 text-arena-700 dark:bg-arena-700/40 dark:border-arena-600/50 dark:text-neon-200/80',
  } as const;
  return <div className={`mx-2 lg:mx-4 rounded-2xl border px-4 py-3 text-[13px] ${M[tone]}`}>{children}</div>;
}

function Info({ k, v, accent }: { k: string; v: string; accent?: 'ok' | 'warn' }) {
  const valCls = accent === 'ok'   ? 'text-emerald-700 dark:text-emerald-300 font-semibold'
              : accent === 'warn' ? 'text-amber-700 dark:text-amber-300 font-semibold'
              : 'text-arena-900 dark:text-white';
  return (
    <div className="flex items-center justify-between gap-2 py-1 border-b border-arena-200/40 dark:border-arena-600/40 last:border-b-0">
      <dt className="text-arena-600 dark:text-neon-200/80">{k}</dt>
      <dd className={`truncate ml-2 ${valCls}`}>{v}</dd>
    </div>
  );
}

// ─── Status buttons (Activate / Suspend / Ban) ─────────────────────
//
// All three buttons are SOLID with proper light+dark colors. The
// active button is shown highlighted; inactive ones are visible
// outlined buttons that the admin can tap to switch.

function StatusButtons({ user, reload, note }: { user: UserFull; reload: () => void; note: (s: string) => void }) {
  const t = useT();
  const [busy, setBusy] = useState<string | null>(null);
  async function setStatus(status: UserFull['status']) {
    setBusy(status);
    try {
      await adminApi.post(`/admin/users/${user.id}/status`, { status });
      // Translated status label so the toast composes naturally per
      // language — `User is now {status}` with {status} pulled from the
      // shared agent.playerDetail.status.* keys.
      note(t('agent.playerDetail.ops.statusBtn.toast', { status: t(`agent.playerDetail.status.${status}`) }));
      reload();
    } catch (e) {
      alert((e as ApiError).message);
    } finally { setBusy(null); }
  }

  // Each button has TWO color modes: "current" (the user's current
  // state — fully filled) and "available" (a tap to change to that
  // state — outlined but visible).
  function Btn({ label, status, currentClass, availableClass, icon }: {
    label: string;
    status: UserFull['status'];
    currentClass: string;
    availableClass: string;
    icon: string;
  }) {
    const isCurrent = user.status === status;
    return (
      <button
        onClick={() => setStatus(status)}
        disabled={busy !== null || isCurrent}
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12.5px] font-semibold transition active:scale-95
                    ${isCurrent ? currentClass : availableClass}
                    disabled:cursor-not-allowed`}
      >
        {busy === status
          ? <Icon name="progress_activity" size={14} className="animate-spin" />
          : <Icon name={icon} size={14} filled />}
        {label}
        {isCurrent && <span className="text-[9.5px] uppercase opacity-75">{t('agent.playerDetail.ops.statusBtn.current')}</span>}
      </button>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      <Btn
        label={t('agent.playerDetail.ops.statusBtn.activate')} status="active" icon="check_circle"
        currentClass="bg-emerald-600 text-white shadow-[0_2px_10px_rgba(16,185,129,0.4)] disabled:opacity-100"
        availableClass="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100
                        dark:bg-emerald-400/15 dark:text-emerald-300 dark:border-emerald-400/30 dark:hover:bg-emerald-400/25"
      />
      <Btn
        label={t('agent.playerDetail.ops.statusBtn.suspend')} status="suspended" icon="pause_circle"
        currentClass="bg-amber-600 text-white shadow-[0_2px_10px_rgba(217,119,6,0.4)] disabled:opacity-100"
        availableClass="bg-amber-50 text-amber-800 border border-amber-300 hover:bg-amber-100
                        dark:bg-amber-400/15 dark:text-amber-200 dark:border-amber-400/30 dark:hover:bg-amber-400/25"
      />
      <Btn
        label={t('agent.playerDetail.ops.statusBtn.ban')} status="banned" icon="block"
        currentClass="bg-rose-600 text-white shadow-[0_2px_10px_rgba(244,63,94,0.4)] disabled:opacity-100"
        availableClass="bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100
                        dark:bg-rose-400/15 dark:text-rose-300 dark:border-rose-400/30 dark:hover:bg-rose-400/25"
      />
    </div>
  );
}

// ─── Delete button (danger zone) ───────────────────────────────────

function DeleteButton({ user, onDeleted, note }: { user: UserFull; onDeleted: () => void; note: (s: string) => void }) {
  const t = useT();
  // Admin-only — if non-admin, render nothing. Backend also gates
  // DELETE /api/admin/users/:id with requireAdminRole; this just
  // hides the UI so agents don't see a button that would 403.
  const { admin } = useAdminAuth();
  if (admin?.role !== 'admin') return null;
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const expected = user.username;
  async function doDelete() {
    if (confirmText !== expected) return;
    setBusy(true);
    try {
      await adminApi.del(`/admin/users/${user.id}`);
      note(t('agent.playerDetail.ops.delete.toast'));
      onDeleted();
    } catch (e) {
      alert((e as ApiError).message);
    } finally { setBusy(false); }
  }
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-rose-600 dark:text-rose-400 mb-1">{t('agent.playerDetail.ops.delete.eyebrow')}</div>
      <p className="text-[11.5px] text-arena-600 dark:text-neon-200/80 mb-2">
        {t('agent.playerDetail.ops.delete.confirm.before')}<b className="text-rose-700 dark:text-rose-300 font-mono">{expected}</b>{t('agent.playerDetail.ops.delete.confirm.after')}
      </p>
      <div className="flex items-center gap-2">
        <input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={expected}
          className={inputCls + ' font-mono flex-1'}
        />
        <button
          onClick={doDelete}
          disabled={busy || confirmText !== expected}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-[12.5px] font-bold
                     bg-rose-600 hover:bg-rose-500 active:bg-rose-700
                     text-white shadow-[0_2px_10px_rgba(244,63,94,0.4)]
                     disabled:opacity-40 disabled:cursor-not-allowed
                     transition active:scale-95"
        >
          {busy ? <Icon name="progress_activity" size={13} className="animate-spin" /> : <Icon name="delete_forever" size={13} />}
          {t('agent.playerDetail.ops.delete.cta')}
        </button>
      </div>
    </div>
  );
}

// ─── Wallet adjust card ────────────────────────────────────────────

function WalletAdjustCard({ userId, onChanged, note }: { userId: number; onChanged: () => void; note: (s: string) => void }) {
  const t = useT();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<'credit' | 'debit'>('credit');
  const [markAsRecharge, setMarkAsRecharge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const amt = Math.round(Number(amount));
    if (!Number.isFinite(amt) || amt <= 0) { setErr(t('agent.playerDetail.walletAdjust.error.positiveAmount')); return; }
    if (!reason.trim()) { setErr(t('agent.playerDetail.walletAdjust.error.reasonRequired')); return; }
    setBusy(true);
    try {
      const r = await adminApi.post<{ referralPayout?: { triggered: boolean; bonus?: number } }>(
        `/admin/users/${userId}/wallet`,
        {
          delta: mode === 'credit' ? amt : -amt,
          reason: reason.trim(),
          markAsRecharge: mode === 'credit' && markAsRecharge,
        }
      );
      // Build the toast from translator-managed verb keys + optional
      // referral suffix. Group 8 directive #4 — compose history copy
      // from building blocks so each language can adjust the verb
      // form without losing the {amount}/{bonus} interpolations.
      let msg = mode === 'credit'
        ? t('agent.playerDetail.walletAdjust.toast.credited', { amount: inr(amt) })
        : t('agent.playerDetail.walletAdjust.toast.debited',  { amount: inr(amt) });
      if (r?.referralPayout?.triggered) {
        msg += t('agent.playerDetail.walletAdjust.toast.referralSuffix', { bonus: inr(r.referralPayout.bonus || 0) });
      }
      note(msg);
      setAmount(''); setReason(''); setMarkAsRecharge(false);
      onChanged();
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setBusy(false); }
  }

  return (
    <Card>
      <h3 className="font-semibold text-[14px] text-arena-900 dark:text-white inline-flex items-center gap-2 mb-3">
        <Icon name="account_balance_wallet" size={16} className="text-violet-700 dark:text-violet-300" />
        {t('agent.playerDetail.walletAdjust.title')}
      </h3>

      {/* Credit / Debit toggle */}
      <div className="inline-flex rounded-full p-1
                      bg-arena-100/60 dark:bg-arena-700/40
                      border border-arena-200/60 dark:border-arena-600/50 mb-3">
        <button type="button" onClick={() => setMode('credit')}
          className={`px-3.5 py-1.5 rounded-full text-[12px] font-bold transition
            ${mode === 'credit'
              ? 'bg-emerald-600 text-white shadow-[0_2px_8px_rgba(16,185,129,0.35)]'
              : 'text-arena-700 hover:text-arena-900 dark:text-neon-200/80 dark:hover:text-white'}`}>
          {t('agent.playerDetail.walletAdjust.mode.credit')}
        </button>
        <button type="button" onClick={() => setMode('debit')}
          className={`px-3.5 py-1.5 rounded-full text-[12px] font-bold transition
            ${mode === 'debit'
              ? 'bg-rose-600 text-white shadow-[0_2px_8px_rgba(244,63,94,0.35)]'
              : 'text-arena-700 hover:text-arena-900 dark:text-neon-200/80 dark:hover:text-white'}`}>
          {t('agent.playerDetail.walletAdjust.mode.debit')}
        </button>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <Field label={t('agent.playerDetail.walletAdjust.field.amount')}>
          <input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)}
                 className={inputCls + ' font-mono'} required />
        </Field>
        <Field label={t('agent.playerDetail.walletAdjust.field.reason')}>
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                 placeholder={t('agent.playerDetail.walletAdjust.field.reason.placeholder')}
                 className={inputCls} required />
        </Field>
        {mode === 'credit' && (
          <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-xl px-3 py-2.5
                            bg-arena-50/40 dark:bg-arena-700/30
                            border border-arena-200/60 dark:border-arena-600/50">
            <input type="checkbox" checked={markAsRecharge}
                   onChange={(e) => setMarkAsRecharge(e.target.checked)}
                   className="mt-0.5 w-4 h-4 accent-violet-500" />
            <div>
              <div className="text-[12.5px] font-semibold text-arena-900 dark:text-white">{t('agent.playerDetail.walletAdjust.markAsRecharge.label')}</div>
              <div className="text-[11px] text-arena-600 dark:text-neon-200/80 mt-0.5">
                {t('agent.playerDetail.walletAdjust.markAsRecharge.body.before')}<b>{t('agent.playerDetail.walletAdjust.markAsRecharge.body.bold')}</b>{t('agent.playerDetail.walletAdjust.markAsRecharge.body.after')}
              </div>
            </div>
          </label>
        )}
        {err && <Banner tone="error">{err}</Banner>}
        <button type="submit" disabled={busy}
                className={`w-full inline-flex items-center justify-center gap-1.5
                            rounded-full px-4 py-2.5 text-[13px] font-bold transition active:scale-95 disabled:opacity-50
                            ${mode === 'credit'
                              ? 'bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white shadow-[0_2px_10px_rgba(16,185,129,0.35)]'
                              : 'bg-rose-600 hover:bg-rose-500 active:bg-rose-700 text-white shadow-[0_2px_10px_rgba(244,63,94,0.35)]'}`}>
          {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="save" size={14} />}
          {busy
            ? t('agent.playerDetail.walletAdjust.cta.applying')
            : t(mode === 'credit' ? 'agent.playerDetail.walletAdjust.cta.applyCredit' : 'agent.playerDetail.walletAdjust.cta.applyDebit')}
        </button>
      </form>
    </Card>
  );
}

// Agent-side replacement for WalletAdjustCard. The admin-only mint
// tool above is hidden for agents (the API hard-rejects role !== 'admin'
// on POST /admin/users/:id/wallet). Agents fund a player only via the
// transactional Path A flow, which debits their own pool inside one DB
// transaction. This card just routes them to the central player-
// requests panel with this player pre-selected via ?preselect=, so
// the entire money-movement UX lives in one place (no inline-form
// duplicates of ManualRechargeForm).
function RechargePlayerCTACard({ userId, username }: { userId: number; username: string | null }) {
  const t = useT();
  const name = username || t('agent.playerDetail.rechargeCta.thisPlayer');
  return (
    <Card>
      <h3 className="font-semibold text-[14px] text-arena-900 dark:text-white inline-flex items-center gap-2 mb-3">
        <Icon name="account_balance_wallet" size={16} className="text-violet-700 dark:text-violet-300" />
        {t('agent.playerDetail.rechargeCta.title')}
      </h3>
      <p className="text-[12px] text-arena-700 dark:text-neon-200/80 mb-4">
        {t('agent.playerDetail.rechargeCta.body.before')}{name}{t('agent.playerDetail.rechargeCta.body.middle')}<b>{t('agent.playerDetail.rechargeCta.body.bold')}</b>{t('agent.playerDetail.rechargeCta.body.after')}
      </p>
      <Link to={`/people/players/requests?preselect=${userId}`}
            className="w-full inline-flex items-center justify-center gap-1.5
                       rounded-full px-4 py-2.5 text-[13px] font-bold transition active:scale-95
                       bg-sky-600 hover:bg-sky-500 active:bg-sky-700 text-white
                       shadow-[0_2px_10px_rgba(14,165,233,0.35)]">
        <Icon name="arrow_outward" size={14} />
        {t('agent.playerDetail.rechargeCta.cta')}
      </Link>
    </Card>
  );
}

// ─── Password reset card ───────────────────────────────────────────

function PasswordResetCard({ userId, note }: { userId: number; note: (s: string) => void }) {
  const t = useT();
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (pwd !== confirm) { setErr(t('agent.playerDetail.security.error.mismatch')); return; }
    if (pwd.length < 8) { setErr(t('agent.playerDetail.security.error.tooShort')); return; }
    setBusy(true);
    try {
      await adminApi.post(`/admin/users/${userId}/reset-password`, { newPassword: pwd });
      note(t('agent.playerDetail.security.toast.reset'));
      setPwd(''); setConfirm('');
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setBusy(false); }
  }

  return (
    <Card>
      <h3 className="font-semibold text-[14px] text-arena-900 dark:text-white inline-flex items-center gap-2 mb-1">
        <Icon name="key" size={16} className="text-amber-700 dark:text-amber-300" />
        {t('agent.playerDetail.security.title')}
      </h3>
      <p className="text-[11.5px] text-arena-600 dark:text-neon-200/80 mb-3">
        {t('agent.playerDetail.security.subtitle')}
      </p>
      <form onSubmit={submit} className="space-y-3">
        <Field label={t('agent.playerDetail.security.field.new')}>
          <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                 className={inputCls} required autoComplete="new-password" />
        </Field>
        <Field label={t('agent.playerDetail.security.field.confirm')}>
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                 className={inputCls} required autoComplete="new-password" />
        </Field>
        {err && <Banner tone="error">{err}</Banner>}
        <button type="submit" disabled={busy}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-full
                           px-4 py-2.5 text-[13px] font-bold
                           bg-amber-600 hover:bg-amber-500 active:bg-amber-700
                           text-white shadow-[0_2px_10px_rgba(245,158,11,0.35)]
                           transition active:scale-95 disabled:opacity-50">
          {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="key" size={14} />}
          {busy
            ? t('agent.playerDetail.security.cta.resetting')
            : t('agent.playerDetail.security.cta.reset')}
        </button>
      </form>
    </Card>
  );
}

// ─── Ledger card ───────────────────────────────────────────────────

function LedgerCard({ userId }: { userId: number }) {
  const t = useT();
  const gameBadge = useGameBadge();
  const [data, setData] = useState<{ rows: LedgerRow[]; total: number } | null>(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const PAGE = 10;

  useEffect(() => {
    setLoading(true);
    adminApi.get<{ rows: LedgerRow[]; total: number }>(`/admin/users/${userId}/ledger?limit=${PAGE}&offset=${page * PAGE}`)
      .then(setData)
      .finally(() => setLoading(false));
  }, [userId, page]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;

  return (
    <Card>
      <h3 className="font-semibold text-[14px] text-arena-900 dark:text-white inline-flex items-center gap-2 mb-3">
        <Icon name="receipt_long" size={16} className="text-arena-600 dark:text-neon-200/80" />
        {t('agent.playerDetail.ledger.title')}
        {data && <span className="ml-auto text-[11px] font-normal text-arena-600 dark:text-neon-200/80">{t(data.total === 1 ? 'agent.playerDetail.ledger.entries.one' : 'agent.playerDetail.ledger.entries.other', { count: data.total })}</span>}
      </h3>

      {loading && (
        <div className="text-[12px] text-arena-600 dark:text-neon-200/80 text-center py-4">{t('agent.playerDetail.ledger.loading')}</div>
      )}
      {!loading && data && (
        <>
          <ul className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
            {data.rows.length === 0 && (
              <li className="text-[12px] text-arena-600 dark:text-neon-200/80 text-center py-4">
                {t('agent.playerDetail.ledger.empty')}
              </li>
            )}
            {data.rows.map((r) => {
              const meta = KIND_META[r.kind] || { icon: 'circle', pos: null };
              const labelKey = KIND_LABEL_KEY[r.kind];
              const label = labelKey ? t(labelKey) : r.kind;
              const positive = (meta.pos ?? (r.amount > 0));
              const gb = gameBadge(r.game);
              return (
                <li key={r.id}
                    className="flex items-center gap-2.5 rounded-xl px-3 py-2.5
                               bg-arena-50/40 dark:bg-arena-700/30
                               border border-arena-200/60 dark:border-arena-600/50">
                  <span className={`grid place-items-center w-7 h-7 rounded-full shrink-0
                    ${positive
                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
                      : 'bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300'}`}>
                    <Icon name={meta.icon} size={14} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[12.5px] font-semibold text-arena-900 dark:text-white">{label}</span>
                      {gb && (
                        <span className={`inline-flex items-center px-1.5 py-0 rounded text-[9.5px] font-bold ${gb.cls}`}>
                          {gb.label}
                        </span>
                      )}
                    </div>
                    {r.note && <div className="text-[11px] text-arena-700 dark:text-neon-200/80 truncate">{r.note}</div>}
                    <div className="text-[10px] text-arena-600 dark:text-neon-200/70">
                      {new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                      {r.actor_admin_id && <span>{t('agent.playerDetail.ledger.actorSuffix', { id: r.actor_admin_id })}</span>}
                    </div>
                  </div>
                  <div className={`text-[13px] font-semibold tabular-nums shrink-0
                    ${positive
                      ? 'text-emerald-700 dark:text-emerald-300'
                      : 'text-rose-700 dark:text-rose-300'}`}>
                    {r.amount > 0 ? '+' : ''}{inr(r.amount)}
                  </div>
                </li>
              );
            })}
          </ul>
          {data.total > PAGE && (
            <div className="mt-3 flex items-center justify-between text-[12px]">
              <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full
                           bg-arena-100/60 hover:bg-arena-200/40
                           dark:bg-arena-700/40 dark:hover:bg-arena-600/55
                           text-arena-700 dark:text-neon-200/80
                           disabled:opacity-40 transition">
                <Icon name="chevron_left" size={14} /> {t('agent.playerDetail.ledger.pagination.prev')}
              </button>
              <span className="text-arena-600 dark:text-neon-200/80 tabular-nums">{page + 1} / {totalPages}</span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full
                           bg-arena-100/60 hover:bg-arena-200/40
                           dark:bg-arena-700/40 dark:hover:bg-arena-600/55
                           text-arena-700 dark:text-neon-200/80
                           disabled:opacity-40 transition">
                {t('agent.playerDetail.ledger.pagination.next')} <Icon name="chevron_right" size={14} />
              </button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ─── Profile editor ────────────────────────────────────────────────

function ProfileEditCard({ user, onSaved, note }: { user: UserFull; onSaved: () => void; note: (s: string) => void }) {
  const t = useT();
  const [form, setForm] = useState(user);
  // R4 final cleanup (Bug B parity) — same structured-geo flow the
  // admin-frontend variant uses. The 7 free-text address inputs that
  // used to live here were a SILENT DATA-LOSS trap: backend
  // updateProfileZ is .strip()-mode (not strict), so submissions of
  // {city, state, ...} would 200 OK while writing nothing. The only
  // way to actually edit a player's location now is the structured
  // geo bag below, which the backend writes via UserModel.adminUpdateProfile
  // + auto-stamps geo_updated_at.
  // Phase R6 (2026-05-13): forward `pincode` string from the backend
  // (users table has always carried pincode_id; the JOIN now returns
  // the 6-digit string aliased to `pincode`) so the LocationCapture
  // saved-summary card shows "Pin code: NNNNNN" immediately without
  // a second round-trip.
  const initialGeo: GeoBag = {
    latitude:    user.latitude    != null ? Number(user.latitude)  : null,
    longitude:   user.longitude   != null ? Number(user.longitude) : null,
    state_id:    user.state_id    ?? null,
    district_id: user.district_id ?? null,
    area_id:     user.area_id     ?? null,
    pincode_id:  user.pincode_id  ?? null,
    pincode:     user.pincode ?? null,
    nation:      user.nation || 'India',
  };
  const [geo, setGeo] = useState<GeoBag>(initialGeo);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setForm(user);
    setGeo({
      latitude:    user.latitude    != null ? Number(user.latitude)  : null,
      longitude:   user.longitude   != null ? Number(user.longitude) : null,
      state_id:    user.state_id    ?? null,
      district_id: user.district_id ?? null,
      area_id:     user.area_id     ?? null,
      pincode_id:  user.pincode_id  ?? null,
      pincode:     user.pincode ?? null,
      nation:      user.nation || 'India',
    });
  }, [user]);

  function set<K extends keyof UserFull>(k: K, v: UserFull[K]) { setForm({ ...form, [k]: v }); }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    const patch: Record<string, unknown> = {};
    // Identity / profile fields — only forward what changed. Legacy
    // address keys (address_line1, city, ...) intentionally ABSENT
    // from this loop: server-side updateProfileZ no longer accepts
    // them, and writing through here would be a silent data-loss
    // trap (Bug B root cause).
    // Phase D4 — username intentionally absent (agents cannot edit it).
    for (const k of ['email','phone','first_name','last_name','birthdate','gender'] as const) {
      if ((form[k] || null) !== (user[k] || null)) {
        patch[k] = form[k];
      }
    }
    // Geo: forward whichever keys differ from server baseline.
    // Sending null IS valid (clears the column); auto-stamps
    // geo_updated_at on the model side.
    const geoKeys: (keyof GeoBag)[] = ['latitude','longitude','state_id','district_id','area_id','pincode_id','nation'];
    let geoTouched = false;
    for (const k of geoKeys) {
      const before = (initialGeo[k] ?? null) as unknown;
      const now    = (geo[k]        ?? null) as unknown;
      if (before !== now) {
        patch[k] = geo[k];
        geoTouched = true;
      }
    }
    if (geoTouched && patch.nation === undefined && geo.nation) {
      patch.nation = geo.nation;
    }
    if (!Object.keys(patch).length) { setBusy(false); note(t('agent.playerDetail.profile.toast.nothingToSave')); return; }
    try {
      await adminApi.patch(`/admin/users/${user.id}`, patch);
      const n = Object.keys(patch).length;
      note(t(n === 1 ? 'agent.playerDetail.profile.toast.saved.one' : 'agent.playerDetail.profile.toast.saved.other', { count: n }));
      onSaved();
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setBusy(false); }
  }

  // Phase R7 follow-up (2026-05-13):
  // Cross-device-consistent date field. iOS Safari completely ignores
  // CSS height/font/text-align on native <input type="date"> — it
  // imposes its own larger touch-friendly box and centered native
  // font, which makes the field look 60% taller than its row-mates.
  // CSS pseudo-element tricks (::-webkit-calendar-picker-indicator)
  // also don't apply on iOS the way they do on desktop Chrome.
  //
  // The robust pattern used here: hide the native <input type="date">
  // (absolute, opacity-0, full-size) so it captures taps and opens
  // the OS-native date picker, AND render a styled <div> overlay
  // that visually mimics a text input. Since the visible element is
  // a plain div, no browser can impose native form-control sizing.
  // Result: the field looks IDENTICAL to a text input on every
  // device (desktop Chrome/Firefox/Safari, iOS Safari, Android Chrome,
  // PWA), and tapping it still opens the OS picker.
  //
  // formatDateISO("2026-05-13") → "13 May 2026" — the friendly
  // format users are already familiar with from iOS native picker.
  const TextInput = (label: string, field: keyof UserFull, type = 'text') => {
    if (type === 'date') {
      const value = (form[field] as string | null) ?? '';
      const display = formatDateISO(value);
      return (
        <Field label={label}>
          <div className="relative">
            {/* Visible styled overlay — pure div, never touched by browser native form styling. */}
            <div className={`${inputCls} flex items-center pr-9 cursor-pointer ${value ? '' : 'text-arena-400 dark:text-neon-200/45'}`}
                 aria-hidden="true">
              {display || 'dd / mm / yyyy'}
            </div>
            <Icon name="calendar_today" size={16}
                  className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none
                             text-arena-500 dark:text-neon-200/60" />
            {/* Invisible native input — captures the tap, opens OS picker. */}
            <input type="date"
                   value={value}
                   onChange={(e) => set(field, (e.target.value || null) as never)}
                   className="absolute inset-0 w-full h-full opacity-0 cursor-pointer
                              dark:[color-scheme:dark]"
                   aria-label={label} />
          </div>
        </Field>
      );
    }
    return (
      <Field label={label}>
        <input type={type}
               value={(form[field] as string | null) ?? ''}
               onChange={(e) => set(field, (e.target.value || null) as never)}
               className={inputCls} />
      </Field>
    );
  };

  // ISO 'YYYY-MM-DD' → 'DD Mmm YYYY' (e.g. '2026-05-13' → '13 May 2026').
  // Mirrors the iOS native picker's display format so users see the
  // same string they're used to. Returns '' for empty/invalid input.
  function formatDateISO(iso: string): string {
    if (!iso) return '';
    const parts = iso.split('-');
    if (parts.length !== 3) return iso;
    const [y, m, d] = parts;
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const mi = parseInt(m, 10) - 1;
    if (mi < 0 || mi > 11) return iso;
    return `${parseInt(d, 10)} ${months[mi]} ${y}`;
  }

  return (
    <Card>
      <h3 className="font-semibold text-[14px] text-arena-900 dark:text-white inline-flex items-center gap-2 mb-4">
        <Icon name="edit" size={16} className="text-arena-600 dark:text-neon-200/80" />
        {t('agent.playerDetail.profile.title')}
      </h3>
      <form onSubmit={submit}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* Phase D4 — Agents may NEVER change a player's username.
              Username is the player's login credential and platform-
              unique identifier; only the admin role can rotate it.
              The agent-side form now renders the username as
              READ-ONLY for context (so the agent knows WHO they're
              editing) but the field is disabled and the patch-builder
              below would refuse to submit it. Backend's
              adminUsers.controller#update also throws 403 if a
              non-admin caller sends `username` in the body. */}
          <Field label={t('agent.playerDetail.profile.field.username') + ' (read-only)'}>
            <input
              type="text"
              value={(form as any).username || ''}
              disabled
              className="w-full rounded-xl px-3 py-2 text-[14px] font-mono tabular-nums
                         bg-arena-100/60 dark:bg-arena-700/30 text-arena-600 dark:text-neon-200/65
                         ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 cursor-not-allowed" />
          </Field>
          {TextInput(t('agent.playerDetail.profile.field.email'),     'email', 'email')}
          {TextInput(t('agent.playerDetail.profile.field.phone'),     'phone')}
          {TextInput(t('agent.playerDetail.profile.field.firstName'), 'first_name')}
          {TextInput(t('agent.playerDetail.profile.field.lastName'),  'last_name')}
          {TextInput(t('agent.playerDetail.profile.field.birthdate'),       'birthdate', 'date')}
          <Field label={t('agent.playerDetail.profile.field.gender')}>
            {/* Phase R7 follow-up (2026-05-13): selects need
                `appearance-none` to remove the OS-native chevron
                (which doesn't respect dark mode), then we overlay
                our own chevron icon via an absolutely-positioned
                <Icon> inside a relative wrapper. `pr-9` reserves
                room for the icon so option text never overlaps it. */}
            <div className="relative">
              <select value={form.gender || ''} onChange={(e) => set('gender', (e.target.value || null) as never)}
                      className={`${inputCls} appearance-none cursor-pointer pr-9`}>
                <option value="">{t('agent.playerDetail.profile.gender.unspecified')}</option>
                <option value="male">{t('agent.playerDetail.profile.gender.male')}</option>
                <option value="female">{t('agent.playerDetail.profile.gender.female')}</option>
                <option value="other">{t('agent.playerDetail.profile.gender.other')}</option>
                <option value="prefer_not">{t('agent.playerDetail.profile.gender.preferNot')}</option>
              </select>
              <Icon name="expand_more" size={18}
                    className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none
                               text-arena-500 dark:text-neon-200/60" />
            </div>
          </Field>
        </div>

        {/* R4 Bug B fix — Location editor (parent agent edits player).
            Replaces the 7 free-text address inputs that used to silently
            fail (server stripped them). hideGps: parent agent's GPS
            would otherwise mis-attribute the player to the parent's
            office coords. Parent uses pincode lookup or manual cascade. */}
        <div className="mt-4 pt-4 border-t border-arena-200/40 dark:border-arena-600/50">
          <div className="text-[11px] font-bold uppercase tracking-wider text-arena-600 dark:text-neon-200/80 mb-2">
            {t('agent.playerDetail.profile.location.title')} {user.geo_updated_at && (
              <span className="font-normal lowercase opacity-60">
                {t('agent.playerDetail.profile.location.lastUpdated', { date: new Date(user.geo_updated_at).toLocaleDateString() })}
              </span>
            )}
          </div>
          <LocationCapture
            key={user.geo_updated_at ?? 'new'}
        value={geo}
            onChange={setGeo}
            mode="child"                     // creator-mode (parent editing someone else); pincode only, no GPS
            disabled={busy}
            inputClassName={inputCls}
          />
        </div>
        {err && <div className="mt-3"><Banner tone="error">{err}</Banner></div>}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <button type="submit" disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12.5px] font-bold
                             bg-violet-600 hover:bg-violet-500 active:bg-violet-700
                             text-white shadow-[0_2px_10px_rgba(139,92,246,0.35)]
                             transition active:scale-95 disabled:opacity-50">
            {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="save" size={14} />}
            {busy ? t('agent.playerDetail.profile.cta.saving') : t('agent.playerDetail.profile.cta.save')}
          </button>
          <button type="button" onClick={() => setForm(user)}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12.5px] font-bold
                             bg-arena-100/60 hover:bg-arena-200/40
                             dark:bg-arena-700/40 dark:hover:bg-arena-600/55
                             text-arena-700 dark:text-neon-200/80
                             border border-arena-200/60 dark:border-arena-600/50
                             transition active:scale-95">
            <Icon name="undo" size={13} /> {t('agent.playerDetail.profile.cta.reset')}
          </button>
        </div>
      </form>
    </Card>
  );
}

// ─── Ownership card + picker ───────────────────────────────────────

function OwnershipCard({ user, reload, note }: { user: UserFull; reload: () => void; note: (s: string) => void }) {
  const t = useT();
  const { admin } = useAdminAuth();
  const [picking, setPicking] = useState(false);
  if (admin?.role !== 'admin') return null;
  const owned = user.agent_id != null;

  return (
    <>
      <div className="mt-4 pt-4 border-t border-arena-200/60 dark:border-arena-600/50">
        <div className="text-[10px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/80 mb-2 flex items-center gap-1.5">
          <Icon name="support_agent" size={13} className="text-emerald-700 dark:text-emerald-300" />
          {t('agent.playerDetail.ownership.row.eyebrow')}
        </div>
        <div className="rounded-xl px-3 py-3 flex items-center gap-3
                        bg-arena-50/40 dark:bg-arena-700/30
                        border border-arena-200/60 dark:border-arena-600/50">
          {owned ? (
            <>
              <AdminAvatar url={user.owner_avatar_url} username={user.owner_username || 'Agent'} size="sm" />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-emerald-700 dark:text-emerald-300 truncate">
                  {(((user as { owner_display_name?: string }).owner_display_name) || '').trim() || t('agent.playerDetail.ownership.owned.fallbackOwner', { id: user.agent_id ?? 0 })}
                </div>
                <div className="text-[10.5px] text-arena-600 dark:text-neon-200/80">
                  {t('agent.playerDetail.ownership.owned.body')}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="grid place-items-center w-9 h-9 rounded-full
                              bg-violet-50 text-violet-700
                              dark:bg-violet-400/15 dark:text-violet-300 shrink-0">
                <Icon name="shield" size={16} filled />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-arena-900 dark:text-white">{t('agent.playerDetail.ownership.platform.title')}</div>
                <div className="text-[10.5px] text-arena-600 dark:text-neon-200/80">
                  {t('agent.playerDetail.ownership.platform.body')}
                </div>
              </div>
            </>
          )}
          <button
            onClick={() => setPicking(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-bold
                       bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700
                       text-white shadow-[0_2px_10px_rgba(16,185,129,0.35)]
                       transition active:scale-95 shrink-0">
            <Icon name="swap_horiz" size={13} /> {t('agent.playerDetail.ownership.cta.change')}
          </button>
        </div>
      </div>
      {picking && (
        <AgentReassignSheet
          subject={{ kind: 'player', label: user.username, currentParentId: user.agent_id ?? null }}
          detach={{
            label: t('agent.playerDetail.ownership.picker.detach.title'),
            description: t('agent.playerDetail.ownership.picker.detach.body'),
            icon: 'shield',
          }}
          onClose={() => setPicking(false)}
          onAssign={async (newParentId, _reason, pickedAgent) => {
            await adminApi.post(`/admin/users/${user.id}/owner`, { agent_id: newParentId });
            setPicking(false);
            note(t('agent.playerDetail.ownership.toast.changedTo', {
              label: pickedAgent
                ? `@${pickedAgent.username}`
                : t('agent.playerDetail.ownership.platformLabel'),
            }));
            reload();
          }}
        />
      )}
    </>
  );
}


// ─── Shared bits ───────────────────────────────────────────────────

const inputCls = `w-full rounded-xl px-3 py-2 text-[14px]
                  bg-white dark:bg-arena-950/60
                  border border-arena-200/60 dark:border-arena-700/60
                  text-arena-900 dark:text-white
                  placeholder-neutral-400 dark:placeholder-white/40
                  focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400/40`;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/80 mb-1 font-semibold">{label}</span>
      {children}
    </label>
  );
}
